import { useEffect, useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../app/ReduxStore.ts'
import { useUpdateUserMutation } from '../../features/SpeechAgendaApiSlice.ts'
import { setNeedsRegistration } from '../../features/FirebaseAuthSlice.ts'
import AuthStep from '../../components/auth/AuthStep.tsx'
import SignInLoadingSpinner from '../../components/login/SignInLoadingSpinner.tsx'
import { saveSignUpDraft } from '../../utils/SignUpDraft.ts'

// Where to land a fresh account when no explicit destination is requested.
const DEFAULT_NEXT = '/clubs'

// Only honor same-origin, absolute in-app paths so `?next=` can't be used to
// bounce the user to an external site. Backslashes are rejected because browsers
// normalize them to slashes, so "/\evil.example" would resolve protocol-relative
// and off-origin despite starting with a single "/".
function sanitizeNext(next: string | null): string {
  if (
    !next ||
    !next.startsWith('/') ||
    next.startsWith('//') ||
    next.includes('\\')
  ) {
    return DEFAULT_NEXT
  }
  return next
}

/**
 * Standalone, club-agnostic registration. Reuses the shared <AuthStep> to
 * materialize a Firebase identity, then calls PUT /user (empty body) to upsert
 * the backend user from the token claims — no club involved. The optional
 * `?next=` query param lets callers (e.g. /register-club) route the user onward
 * once the account exists.
 */
export default function SignUp() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const next = sanitizeNext(searchParams.get('next'))

  const firebaseUser = useSelector((state: RootState) => state.firebaseAuth.user)

  const [pendingProvision, setPendingProvision] = useState(false)
  const [provisioning, setProvisioning] = useState(false)
  const [provisionError, setProvisionError] = useState(false)

  const [updateUser] = useUpdateUserMutation()

  const runProvision = async () => {
    setProvisioning(true)
    setProvisionError(false)
    try {
      await updateUser().unwrap()
      dispatch(setNeedsRegistration(false))
      navigate(next)
    } catch {
      dispatch(setNeedsRegistration(false))
      setProvisioning(false)
      setProvisionError(true)
    }
  }

  // Synchronous auth (Google / email+password) only signals intent; we wait for
  // the Firebase token to land in the store so prepareHeaders can attach it
  // before PUT /user runs.
  useEffect(() => {
    if (pendingProvision && firebaseUser && !provisioning) {
      setPendingProvision(false)
      void runProvision()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProvision, firebaseUser, provisioning])

  const handleBeforePasswordless = (email: string, displayName: string) => {
    // Carry the redirect target across the email round-trip; the completion
    // route reads it back from the draft.
    saveSignUpDraft({ email, displayName, next })
    return true
  }

  if (provisioning) {
    return <SignInLoadingSpinner message="Setting up your account, please wait..." />
  }

  return (
    <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-10 w-auto"
          style={{ width: '60px', height: '60px' }}
          src="/logo.svg"
          alt="Speech Agenda"
        />
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign up to get started with Speech Agenda.
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md">
        {provisionError && (
          <p className="mb-4 text-sm text-red-600">
            Something went wrong while setting up your account. Please try again.
          </p>
        )}

        <AuthStep
          onAuthenticated={() => setPendingProvision(true)}
          onBeforePasswordless={handleBeforePasswordless}
        />

        <div className="mt-8 flex items-center justify-center">
          <div className="text-sm leading-6">
            <span className="text-gray-600">Already have an account? </span>
            <NavLink
              to="/login"
              className="font-semibold text-indigo-600 hover:text-indigo-500">
              Sign in
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  )
}
